@if (task()) {
  <div class="space-y-4 pb-3">
    <!-- Task Info Section -->
    <div class="space-y-3">
      <!-- Description -->
      @if (task()?.description) {
        <div class="p-3 bg-neutral-50 dark:bg-neutral-800 rounded-md">
          <p class="text-xs text-neutral-600 dark:text-neutral-300">{{ task()!.description }}</p>
        </div>
      }

      <!-- Status and Priority -->
      <div class="flex gap-2">
        <div class="flex-1">
          <span class="text-xs text-neutral-500 dark:text-neutral-400 block mb-1">Status</span>
          <span
            class="px-2 py-1 rounded text-xs font-medium"
            [ngClass]="{
              'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300':
                task()?.status === 'pending',
              'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300':
                task()?.status === 'in_progress',
              'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300':
                task()?.status === 'completed',
              'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300':
                task()?.status === 'cancelled',
            }"
          >
            {{ getStatusText(task()!.status) }}
          </span>
        </div>
        <div class="flex-1">
          <span class="text-xs text-neutral-500 dark:text-neutral-400 block mb-1">Priority</span>
          <span
            class="px-2 py-1 rounded text-xs font-medium"
            [ngClass]="{
              'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300':
                task()?.priority === 'high',
              'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300':
                task()?.priority === 'medium',
              'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300':
                task()?.priority === 'low',
            }"
          >
            {{ task()!.priority }}
          </span>
        </div>
      </div>

      <!-- Dates -->
      @if (task()?.date || task()?.due_date) {
        <div class="flex gap-2 text-xs">
          @if (task()?.date) {
            <div class="flex-1">
              <span class="text-neutral-500 dark:text-neutral-400 block mb-1">Start Date</span>
              <span class="text-neutral-700 dark:text-neutral-200">{{ task()!.date }}</span>
            </div>
          }
          @if (task()?.due_date) {
            <div class="flex-1">
              <span class="text-neutral-500 dark:text-neutral-400 block mb-1">Due Date</span>
              <span class="text-neutral-700 dark:text-neutral-200">{{ task()!.due_date }}</span>
            </div>
          }
        </div>
      }

      <!-- Tags -->
      @if (task()?.tags && task()!.tags.length > 0) {
        <div>
          <span class="text-xs text-neutral-500 dark:text-neutral-400 block mb-1">Tags</span>
          <div class="flex flex-wrap gap-1">
            @for (tag of task()!.tags; track tag) {
              <span
                class="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs"
              >
                {{ tag }}
              </span>
            }
          </div>
        </div>
      }
    </div>

    <div class="border-t border-neutral-200 dark:border-neutral-600"></div>

    <!-- Todo Lists Section -->
    <div class="space-y-3">
      <h3
        class="text-sm font-semibold text-neutral-700 dark:text-neutral-300 flex items-center gap-2"
      >
        <i class="fa-light fa-list-check"></i>
        Todo Lists
      </h3>

      <!-- Existing Todo Lists -->
      @if (task()?.todo_lists && task()!.todo_lists.length > 0) {
        <div class="space-y-3">
          @for (todoList of task()!.todo_lists; track todoList.uuid) {
            <div
              class="border border-neutral-200 dark:border-neutral-600 rounded-md overflow-hidden"
            >
              <!-- Todo List Header -->
              <div
                class="bg-neutral-100 dark:bg-neutral-700 px-3 py-2 flex items-center justify-between"
              >
                <div class="flex-1">
                  <h4 class="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                    {{ todoList.title }}
                  </h4>
                  @if (todoList.list.length > 0) {
                    <div class="mt-1">
                      <div class="flex items-center gap-2">
                        <div
                          class="flex-1 h-1.5 bg-neutral-200 dark:bg-neutral-600 rounded-full overflow-hidden"
                        >
                          <div
                            class="h-full bg-green-500 transition-all duration-300"
                            [style.width.%]="getTodoProgress(todoList).percentage"
                          ></div>
                        </div>
                        <span class="text-xs text-neutral-500 dark:text-neutral-400">
                          {{ getTodoProgress(todoList).completed }}/{{
                            getTodoProgress(todoList).total
                          }}
                        </span>
                      </div>
                    </div>
                  }
                </div>
                <button
                  (click)="deleteTodoList(todoList); $event.stopPropagation()"
                  type="button"
                  class="p-1 text-neutral-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                >
                  <i class="fa-light fa-trash text-xs"></i>
                </button>
              </div>

              <!-- Todo Items -->
              <div class="p-2 space-y-1">
                @for (todoItem of todoList.list; track todoItem.uuid) {
                  <div
                    class="flex items-center gap-2 p-2 rounded hover:bg-neutral-50 dark:hover:bg-neutral-700 group"
                  >
                    <p-checkbox
                      [(ngModel)]="todoItem.status"
                      [binary]="false"
                      [trueValue]="'completed'"
                      [falseValue]="'pending'"
                      (onChange)="toggleTodoItem(todoList, todoItem)"
                    >
                    </p-checkbox>
                    <span
                      class="flex-1 text-sm text-neutral-700 dark:text-neutral-200"
                      [class.line-through]="todoItem.status === 'completed'"
                      [class.text-neutral-400]="todoItem.status === 'completed'"
                    >
                      {{ todoItem.title }}
                    </span>
                    <button
                      (click)="deleteTodoItem(todoList, todoItem); $event.stopPropagation()"
                      type="button"
                      class="opacity-0 group-hover:opacity-100 p-1 text-neutral-400 hover:text-red-500 dark:hover:text-red-400 transition-all"
                    >
                      <i class="fa-light fa-times text-xs"></i>
                    </button>
                  </div>
                }

                <!-- Add Todo Item -->
                <div class="flex gap-2 pt-1">
                  <input
                    type="text"
                    [ngModel]="getTodoItemTitle(todoList.uuid)"
                    (ngModelChange)="setTodoItemTitle(todoList.uuid, $event)"
                    (click)="$event.stopPropagation()"
                    (keypress)="$event.key === 'Enter' && addTodoItem(todoList)"
                    placeholder="Add todo item..."
                    class="flex-1 px-2 py-1 text-xs bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                  <button
                    (click)="addTodoItem(todoList)"
                    type="button"
                    class="px-2 py-1 bg-primary-500 text-white rounded hover:bg-primary-600 transition-colors"
                  >
                    <i class="fa-light fa-plus text-xs"></i>
                  </button>
                </div>
              </div>
            </div>
          }
        </div>
      }

      <!-- Add New Todo List -->
      <div class="border border-dashed border-neutral-300 dark:border-neutral-600 rounded-md p-3">
        <div class="flex gap-2">
          <input
            type="text"
            [(ngModel)]="newTodoListTitle"
            (click)="$event.stopPropagation()"
            (keypress)="$event.key === 'Enter' && addTodoList()"
            (keydown)="$event.stopPropagation()"
            placeholder="New todo list title..."
            class="flex-1 px-3 py-1.5 text-sm bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <button
            (click)="addTodoList()"
            type="button"
            class="px-3 py-1.5 bg-primary-500 text-white rounded hover:bg-primary-600 transition-colors text-sm"
          >
            <i class="fa-light fa-plus me-1"></i>
            Add List
          </button>
        </div>
      </div>
    </div>
  </div>
}
